import { useTranslation } from 'react-i18next';
import { Loader2, CircleCheck, OctagonX } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { FormSelect } from '@/shared/ui/form';
import { FormField } from '@/shared/ui';

import { usePreferencesForm } from '../model';

interface PreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PreferencesModal = ({ open, onOpenChange }: PreferencesModalProps) => {
  const { t } = useTranslation();

  const { form, onSubmit, alertState, isLoading, availableLanguages } = usePreferencesForm({ open });

  const {
    control,
    handleSubmit,
    formState: { isDirty }
  } = form;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('preferences.title', 'Please Set your preferences')}</DialogTitle>
        </DialogHeader>

        {alertState && (
          <Alert variant={alertState.type === 'error' ? 'destructive' : 'success'} className="mb-4">
            {alertState.type === 'error' ? <OctagonX className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
            <AlertTitle>{alertState.type === 'error' ? 'Error' : 'Success'}</AlertTitle>
            <AlertDescription>{alertState.message}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="py-2">
          <FormField label={t('preferences.language.label', 'Language')} name="languageCode">
            <FormSelect
              name="languageCode"
              control={control}
              placeholder={t('language.select', 'Select Language')}
              options={availableLanguages.map((lang) => ({
                value: lang.code,
                label: `${lang.flag ? lang.flag + ' ' : ''}${lang.label}`
              }))}
            />
          </FormField>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.closeDialog', 'Close Dialog')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isLoading || !isDirty}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.saveChanges', 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
